import { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { AbilityContext } from "context/Ability.context";
import { selectThemeMode, setThemeMode } from "state/application/applicationSlice";
import { useGetCommunitiesByUserQuery } from "state/communities/communitiesApiSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectCurrentUser } from "state/auth/authSlice";

import { avColor, initials } from "utils/avatar";
import { useIsMobile } from "utils/hooks/useBreakpoint";

import Icon from "./Icon";
import LanguageMenu from "./LanguageMenu";

function activateOnKey(handler) {
  return (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handler(e);
    }
  };
}

export default function Topbar({ onToggleSidebar }) {
  const { t } = useTranslation(["sidenav", "common"]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeMode = useSelector(selectThemeMode);
  const community = useSelector(selectCurrentCommunity);
  // The current community is persisted in localStorage and may be stale (e.g.
  // saved before the backend started returning `identifier`). Fall back to the
  // fresh communities-list cache so the header self-heals without re-selecting.
  const { freshIdentifier, freshMembers } = useGetCommunitiesByUserQuery(undefined, {
    skip: !community?.id,
    selectFromResult: ({ data }) => {
      // `data` is an entity-adapter result ({ ids, entities }), not an array.
      const fresh = community?.id ? data?.entities?.[community.id] : undefined;
      return { freshIdentifier: fresh?.identifier, freshMembers: fresh?.total_members };
    },
  });
  const communityIdentifier = community?.identifier || freshIdentifier || null;
  const communityMembers = community?.total_members ?? freshMembers ?? null;
  const user = useSelector(selectCurrentUser);
  const ability = useContext(AbilityContext);
  const canSeeStructure = community && ability.can("manage", "all");
  const isMobile = useIsMobile();

  const toggleTheme = () => dispatch(setThemeMode(themeMode === "dark" ? "light" : "dark"));

  const goToCommunities = () => navigate("/communities");

  // User dropdown (settings / logout)
  const [userMenu, setUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  useEffect(() => {
    if (!userMenu) return undefined;
    const close = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [userMenu]);

  return (
    <header className="rec-topbar">
      <button
        type="button"
        className="rec-icon-btn"
        onClick={onToggleSidebar}
        title={t("topbar.toggle", {
          ns: "sidenav",
          defaultValue: "Toggle menu",
        })}
        style={{ marginLeft: -6 }}
      >
        <Icon name="menu" />
      </button>

      {community && (
        <div
          className="rec-community-switch"
          role="button"
          tabIndex={0}
          onClick={goToCommunities}
          onKeyDown={activateOnKey(goToCommunities)}
          title={t("topbar.switchCommunity", {
            ns: "sidenav",
            defaultValue: "Switch community",
          })}
        >
          <div className="rec-cs-ic">
            <Icon name="solar_power" fill />
          </div>
          <div className="rec-cs-txt">
            <b>{community.name}</b>
            <small>
              {[
                communityMembers != null
                  ? t("topbar.members", {
                      ns: "sidenav",
                      count: communityMembers,
                      defaultValue: "{{count}} members",
                    })
                  : null,
                communityIdentifier,
              ]
                .filter(Boolean)
                .join(" · ")}
            </small>
          </div>
          <Icon name="unfold_more" style={{ fontSize: 18, color: "var(--ink-3)" }} />
        </div>
      )}

      <div style={{ flex: 1 }} />

      {canSeeStructure && !isMobile && (
        <button
          type="button"
          className="rec-icon-btn"
          onClick={() => navigate(`/communities/${community.id}/structure`)}
          title={t("structure", { ns: "sidenav", defaultValue: "Estrutura" })}
        >
          <Icon name="account_tree" />
        </button>
      )}

      {!isMobile && <LanguageMenu />}

      <button
        type="button"
        className="rec-icon-btn"
        onClick={toggleTheme}
        title={t("topbar.toggleTheme", {
          ns: "sidenav",
          defaultValue: "Toggle theme",
        })}
      >
        <Icon name={themeMode === "dark" ? "light_mode" : "dark_mode"} fill />
      </button>

      <div
        aria-hidden="true"
        style={{
          width: 1,
          height: 30,
          background: "var(--border)",
          margin: "0 4px",
        }}
      />

      <div className="relative" ref={userMenuRef}>
        <button
          type="button"
          className="flex items-center gap-[10px] bg-transparent border-0 p-0 cursor-pointer"
          onClick={() => setUserMenu((m) => !m)}
          aria-haspopup="menu"
          aria-expanded={userMenu}
          title={user?.name || undefined}
        >
          <span
            className="rec-avatar"
            style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              fontSize: 14,
              background: avColor(user?.name || user?.email || ""),
            }}
            aria-label={user?.name || "User"}
          >
            {initials(user?.name || user?.email || "")}
          </span>
          <span className="leading-[1.2] hidden sm:block text-left">
            <span className="block text-[13px] font-semibold text-ink">{user?.name || "—"}</span>
            <span className="block text-[11px] text-ink-3">{user?.email || ""}</span>
          </span>
          <Icon name="unfold_more" style={{ fontSize: 17, color: "var(--ink-3)" }} />
        </button>
        {userMenu && (
          <div className="rec-pop !min-w-[200px]">
            {isMobile && (
              <>
                <LanguageMenu variant="inline" />
                <div className="my-1 border-t border-border" />
              </>
            )}
            <button
              type="button"
              className="rec-pop-opt"
              onClick={() => {
                setUserMenu(false);
                navigate("/settings");
              }}
            >
              <Icon name="settings" style={{ fontSize: 17 }} />
              {t("topbar.settings", { ns: "sidenav", defaultValue: "Definições" })}
            </button>
            <button
              type="button"
              className="rec-pop-opt"
              style={{ color: "var(--danger)" }}
              onClick={() => {
                setUserMenu(false);
                navigate("/signout");
              }}
            >
              <Icon name="logout" style={{ fontSize: 17 }} />
              {t("topbar.logout", { ns: "sidenav", defaultValue: "Sair" })}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

Topbar.propTypes = {
  onToggleSidebar: PropTypes.func.isRequired,
};
